import React from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputField from "../../../form/InputField";
import SubTitle from "../../../ui/SubTitle";
import CategoryBoxSelect from "./CategoryBoxSelect";
import ProductBox from "./ProductBoxSelect";
import { Product } from "../../../../types/modules/product.type";
import Pagination from "../../../ui/Pagination";

export type OfferFormValues = {
  name: string;
  type: "fixed" | "percentage" | "incentive";
  discountAmount?: number;
  discountPercentage?: number;
  buyQty?: number;
  getFreeQty?: number;
  duration: number;
  durationUnit: "hours" | "days";
  startDate: string;
  target: "product" | "category" | "all";
  productIds?: number[];
  categoryIds?: number[];
};

interface Props {
  register: UseFormRegister<OfferFormValues>;
  errors: FieldErrors<OfferFormValues>;
  watchType: "fixed" | "percentage" | "incentive";
  watchTarget: "all" | "product" | "category";
  watchDurationUnit: "hours" | "days";
  setValue: (field: keyof OfferFormValues, value: any) => void;
  selectedProducts?: { id: number }[];
  selectedCategories?: { id: number}[];
  availableProducts: Product[];
  availableCategories: { id: number; name: string }[];
  onSelectProduct?: (product: any) => void;
  onSelectCategory?: (category: any) => void;
  onRemoveProduct?: (id: number) => void;
  onRemoveCategory?: (id: number) => void;
  currentPage:number,
  totalPages:number,
  handlePagination: (page: number) => void
}

const AddOfferForm: React.FC<Props> = ({
  register,
  errors,
  watchType,
  watchTarget,
  watchDurationUnit,
  setValue,
  selectedProducts = [],
  selectedCategories = [],
  availableProducts = [],
  availableCategories = [],
  onSelectProduct,
  onSelectCategory,
  onRemoveProduct,
  onRemoveCategory,
  currentPage,
  totalPages,
  handlePagination
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">

      {/* Offer Name */}
      <div>
        <SubTitle title="offer_table.offer_name"/>
        <InputField
          placeholder={t("offer_table.offer_name")}
          register={register("name", { required: t("required_field") })}
          error={errors.name}
        />
      </div>

      {/* Offer Type */}
      <div>
        <SubTitle title="select_offer_type"/>
        <div className="grid grid-cols-2 gap-8 ">
          {watchType === "fixed" && (
            <InputField
              placeholder={t("discount_amount")}
              type="number"
              register={register("discountAmount", { valueAsNumber: true ,min:1})}
              error={errors.discountAmount}
            />
          )}
          {watchType === "percentage" && (
            <InputField
              placeholder={t("discount_percentage")}
              type="number"
              register={register("discountPercentage", { valueAsNumber: true })}
              error={errors.discountPercentage}
            />
          )}
          {watchType === "incentive" && (
            <div className="flex gap-3 w-full">
              <div className="w-full">
                <InputField
                  placeholder={t("buy_quantity")}
                  type="number"
                  register={register("buyQty", { valueAsNumber: true })}
                  error={errors.buyQty}
                />
              </div>
              <div className="w-full">
                <InputField
                  placeholder={t("get_free_quantity")}
                  type="number"
                  register={register("getFreeQty", { valueAsNumber: true })}
                  error={errors.getFreeQty}
                />
              </div>
            </div>
          )}
          <div className="flex gap-3 mb-3">
            {["fixed", "percentage", "incentive"].map((type) => (
              <button
                type="button"
                key={type}
                className={`px-4 py-2 rounded-2xl ${
                  watchType === type ? "bg-primaryButton text-white" : " bg-LightGrey1 text-DarkGrey3"
                }`}
                onClick={() => setValue("type", type)}
              >
                {t(type)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Duration */}
      <div>
        <SubTitle title="duration"/>
        <div className="flex gap-3 mb-3">
          <input
            className={`w-full p-3 rounded-[8px] border ${
              errors.duration ? 'border-danger' : 'border-muted'
            } focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-sm`} 
            type="number"
            placeholder={t("duration")}
            {...register("duration", { valueAsNumber: true, required: t("required_field") })}
          />
          {["days", "hours"].map((unit) => (
            <button
              type="button"
              key={unit}
              className={`px-4 py-2 rounded-2xl ${
                watchDurationUnit === unit ? "bg-primaryButton text-white" : " bg-LightGrey1 text-DarkGrey3"
              }`}
              onClick={() => setValue("durationUnit", unit)}
            >
              {t(unit)}
            </button>
          ))}
        </div>
      </div>

      {/* Start Date */}
      <div>
        <SubTitle title="start_day"/>
        <InputField
          placeholder={t("start_date")}
          type="datetime-local"
          register={register("startDate", { required: t("required_field") })}
          error={errors.startDate}
        />
      </div>

      {/* Target */}
      <div>
        <SubTitle title="select_offer_products"/>
        <div className="flex gap-3 mb-3">
          {["all", "product", "category"].map((tgt) => (
            <button
              type="button"
              key={tgt}
              className={`px-4 py-2 rounded-2xl capitalize ${
                watchTarget === tgt ? "bg-primaryButton text-white" : " bg-LightGrey1 text-DarkGrey3"
              }`}
              onClick={() => setValue("target", tgt)}
            >
              {t(`${tgt}_target`)}
            </button>
          ))}
        </div>

        {watchTarget === "product" && (
          <div>
            <div className="mt-5 flex justify-between">
              <SubTitle title="select_products"/>
              <span className="text-DarkGrey3 text-[12px] font-medium">
                {selectedProducts.length} {t('items_selected')}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {availableProducts.map((prod) => {
                const isSelected = selectedProducts.some(sp => sp.id === prod.id);
                return (
                  <ProductBox
                    key={prod.id}
                    product={prod}
                    isSelected={isSelected}
                    onSelect={(p) => {
                      onSelectProduct?.(p);
                      setValue("productIds", [...selectedProducts.map(p => p.id), p.id]);
                    }}
                    onRemove={(id) => {
                      onRemoveProduct?.(id);
                      setValue("productIds", selectedProducts.filter(p => p.id !== id).map(p => p.id));
                    }}
                  />
                );
              })}
            </div>

            <Pagination
            handlePagination={handlePagination}
            totalPages={totalPages}
            currentPage={currentPage}
            />
          </div>
        )}

        {watchTarget === "category" && (
          <div>
            <div className="mt-5">
              <SubTitle title="select_categories"/>
            </div>
            <div className="flex flex-col gap-2">
              {availableCategories.map((cat) => {
                const isSelected = selectedCategories.some(sc => sc.id === cat.id);
                return (
                  <CategoryBoxSelect
                    key={cat.id}
                    category={cat}
                    isSelected={isSelected}
                    onSelect={(c) => {
                      onSelectCategory?.(c);
                      setValue("categoryIds", [...selectedCategories.map(c => c.id), c.id]);
                    }}
                    onRemove={(id) => {
                      onRemoveCategory?.(id);
                      setValue("categoryIds", selectedCategories.filter(c => c.id !== id).map(c => c.id));
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOfferForm;
